"use client";

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { AppLocale } from "@/lib/i18n/config";
import { DEFAULT_LOCALE, normalizeAppLocale } from "@/lib/i18n/config";
import { translateKey } from "@/lib/i18n/dictionaries";
import { readLocaleCookie, writeLocaleCookie } from "@/lib/i18n/cookie";

type LocaleContextValue = {
  locale: AppLocale;
  setLocale: (next: AppLocale) => Promise<void>;
  /** Sync cookie + React locale after profile (or other server) already saved — updates all `t()` immediately. */
  applyLocale: (next: AppLocale) => void;
  t: (key: string) => string;
  persistToProfile: boolean;
};

const LocaleContext = createContext<LocaleContextValue | null>(null);

type Props = {
  children: React.ReactNode;
  initialLocale: AppLocale | null;
  persistToProfile: boolean;
};

export function LocaleProvider({ children, initialLocale, persistToProfile }: Props) {
  const [locale, setLocaleState] = useState<AppLocale>(() => {
    if (initialLocale) return initialLocale;
    if (typeof window !== "undefined") {
      return normalizeAppLocale(readLocaleCookie());
    }
    return DEFAULT_LOCALE;
  });

  useEffect(() => {
    if (initialLocale) {
      setLocaleState(initialLocale);
      writeLocaleCookie(initialLocale);
    }
  }, [initialLocale]);

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  const applyLocale = useCallback((next: AppLocale) => {
    const normalized = normalizeAppLocale(next);
    setLocaleState(normalized);
    writeLocaleCookie(normalized);
  }, []);

  const setLocale = useCallback(
    async (next: AppLocale) => {
      const normalized = normalizeAppLocale(next);
      setLocaleState(normalized);
      writeLocaleCookie(normalized);
      if (persistToProfile) {
        try {
          await fetch("/api/profile/locale", {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ locale: normalized }),
          });
        } catch {
          /* cookie still updated */
        }
      }
    },
    [persistToProfile]
  );

  const t = useCallback((key: string) => translateKey(locale, key), [locale]);

  const value = useMemo(
    () => ({ locale, setLocale, applyLocale, t, persistToProfile }),
    [locale, setLocale, applyLocale, t, persistToProfile]
  );

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocaleContext(): LocaleContextValue {
  const ctx = useContext(LocaleContext);
  if (!ctx) {
    return {
      locale: DEFAULT_LOCALE,
      setLocale: async () => {},
      applyLocale: () => {},
      t: (key) => translateKey(DEFAULT_LOCALE, key),
      persistToProfile: false,
    };
  }
  return ctx;
}
